import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/errors';
import { toast } from '@/lib/toast';

import { sessionsService } from '../services';
import type { Session } from '../types';

import { sessionsKeys } from './useSessions';

export interface UpdateSessionConcernsInput {
  /** At least one entry. The BE rejects an empty array: a completed session
   *  must carry signal for the org concern aggregations. */
  concerns: string[];
}

/**
 * Mutation hook for PATCH /sessions/:id/concerns. Retags the categorical
 * concerns on an already-completed session.
 *
 * Consumers must gate the affordance on `session.canEditConcerns` from the
 * detail payload. The BE restricts the write to the session's clinical owner,
 * their lead at (locationId, projectKey), or a chief at the location; a caller
 * who is none of those gets a 403 surfaced as a toast here.
 *
 * The BE returns the full detail shape (it re-reads through getSessionById), so
 * the response is seeded straight into the detail cache before invalidating.
 * Without the seed the chips would flash their pre-edit values for one refetch.
 *
 * Invalidates detail + lists. Deliberately does NOT touch the org-analytics
 * query keys: those live on a different (org_user) surface with its own
 * refetch cycle, and the editing staff member never has both mounted.
 */
export function useUpdateSessionConcerns(id: string) {
  const qc = useQueryClient();
  return useMutation<Session, Error, UpdateSessionConcernsInput>({
    mutationFn: (input) => sessionsService.updateConcerns(id, input.concerns),
    onSuccess: (session) => {
      qc.setQueryData(sessionsKeys.detail(id), session);
      void qc.invalidateQueries({ queryKey: sessionsKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: sessionsKeys.lists() });
      toast.updated();
      return session;
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not update concerns.');
    },
  });
}
